// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import {
  approveLogoRequest,
  changeTeamStatus,
  deleteTeam,
  getPendingLogoRequests,
  getTeamDetail,
  getTeamList,
  refuseLogoRequest,
} from '@/modules/team/team.service'
import type { TChangeTeamStatusRequest } from '@/modules/team/team.types'

export const teamListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['teamList', params],
    queryFn: () => getTeamList(params),
  })

export const teamDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['teamDetail', id],
    queryFn: () => getTeamDetail(id),
    staleTime: 0,
    enabled: Boolean(id),
  })

export function useChangeTeamStatus({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TChangeTeamStatusRequest>) {
  return useMutation<void, TResponseError, TChangeTeamStatusRequest>({
    mutationFn: changeTeamStatus,
    onSuccess,
    onError,
  })
}

export function useDeleteTeam({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deleteTeam,
    onSuccess,
    onError,
  })
}

export const pendingLogoRequestsQueryOptions = () =>
  queryOptions({
    queryKey: ['pendingTeamLogoRequests'],
    queryFn: getPendingLogoRequests,
    staleTime: 0,
  })

export function useApproveLogoRequest({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: approveLogoRequest,
    onSuccess,
    onError,
  })
}

export function useRefuseLogoRequest({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, { id: string; refuseReason: string }>) {
  return useMutation<
    void,
    TResponseError,
    { id: string; refuseReason: string }
  >({
    mutationFn: refuseLogoRequest,
    onSuccess,
    onError,
  })
}
